import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus('error');
      setMessage('Token di verifica mancante. Controlla il link nella tua email.');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.post(
        `${API}/api/auth/verify-email`,
        { token },
        { withCredentials: true }
      );
      
      setStatus('success');
      setMessage(response.data.message);
      setUserData(response.data);
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Errore durante la verifica';
      
      if (detail.includes('scaduto')) {
        setStatus('expired');
        setMessage('Il link di verifica è scaduto. Richiedi un nuovo link.');
      } else {
        setStatus('error');
        setMessage(detail);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === 'loading' && (
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            )}
            {(status === 'error' || status === 'expired') && (
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            )}
          </div>
          
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {status === 'loading' && 'Verifica in corso...'}
            {status === 'success' && 'Email Verificata!'}
            {status === 'error' && 'Verifica Fallita'}
            {status === 'expired' && 'Link Scaduto'}
          </CardTitle>
          
          <CardDescription className="text-base mt-2">
            {message}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {status === 'success' && userData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800 font-medium">
                Benvenuto, {userData.name}!
              </p>
              <p className="text-green-600 text-sm mt-1">
                Verrai reindirizzato alla dashboard...
              </p>
              <Button 
                className="mt-4 bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/dashboard')}
              >
                Vai alla Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
          
          {status === 'expired' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 text-center">
                Il link di verifica è valido solo per 24 ore.
              </p>
              <Button 
                className="w-full"
                onClick={() => navigate('/login?resend=true')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Richiedi nuovo link
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-3">
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Torna al Login
              </Button>
              <Button 
                className="w-full"
                onClick={() => navigate('/register')}
              >
                Registrati di nuovo
              </Button>
            </div>
          )}
          
          {status === 'loading' && (
            <div className="text-center text-slate-500 text-sm">
              Attendere prego...
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Footer */}
      <div className="fixed bottom-4 text-center w-full">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-[#0055BB]">Assietica Gest</span> – by Assietica
        </p>
      </div>
    </div>
  );
}
